import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiRequest, { API_URLS } from "../services/api";
import Alert from "../components/Alert";

const Icon = ({ d, className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={d} />
  </svg>
);

const ICONS = {
  arrowLeft: "M10 19l-7-7m0 0l7-7m-7 7h18",
  questions: "M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  plus:      "M12 4v16m8-8H4",
  edit:      "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
  trash:     "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16",
  warning:   "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
  x:         "M6 18L18 6M6 6l12 12",
  text:      "M4 6h16M4 12h16M4 18h7",
  textarea:  "M4 6h16M4 12h16m-7 6h7",
  number:    "M7 20l4-16m2 16l4-16M6 9h14M4 15h14",
  select:    "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
  check:     "M5 13l4 4L19 7",
  hash:      "M7 20l4-16m2 16l4-16M6 9h14M4 15h14",
  search:    "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
};

const QUESTION_TYPES = [
  { value: "text",            label: "Short Text",                 icon: "M4 6h16M4 12h16M4 18h7"                                                                                                                             },
  { value: "textarea",        label: "Long Text",                  icon: "M4 6h16M4 12h16m-7 6h7"                                                                                                                             },
  { value: "number",          label: "Number",                     icon: "M7 20l4-16m2 16l4-16M6 9h14M4 15h14"                                                                                                                },
  { value: "select",          label: "Multiple Choice (Single)",   icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"                },
  { value: "multiple_select", label: "Multiple Choice (Multiple)", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"                },
];

const PALETTE = [
  "bg-blue-600","bg-emerald-600","bg-violet-600","bg-indigo-600",
  "bg-amber-500","bg-teal-600","bg-rose-600","bg-cyan-600",
  "bg-orange-500","bg-purple-600",
];

const EMPTY_FORM = { question: "", type: "text", options: [], parent_id: null, option_text: null };

const inputCls = "w-full px-3.5 py-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition bg-slate-50 focus:bg-white";

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

// ─── Modal shell ─────────────────────────────────────────────────────────────
const Modal = ({ icon, iconBg = "bg-blue-600", title, subtitle, onClose, children, footer }) => (
  <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
      <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 shrink-0">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 ${iconBg} rounded-lg flex items-center justify-center shrink-0`}>
            <Icon d={icon} className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900">{title}</h2>
            {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
          </div>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
          <Icon d={ICONS.x} className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">{children}</div>
      {footer && <div className="px-6 pb-6 pt-2 shrink-0">{footer}</div>}
    </div>
  </div>
);

// ─── QuestionFormBody ─────────────────────────────────────────────────────────
// MUST be outside the page component — defining it inside causes React to treat
// it as a new component type on every render, unmounting/remounting the inputs
// on each keystroke (causing RTL text, focus loss, and value bleed).
const QuestionFormBody = ({ formData, onFieldChange, onTypeChange, onAddOption, onUpdateOption, onRemoveOption, questions, isSubQuestion = false }) => {
  const isChoiceType = ["select", "multiple_select"].includes(formData.type);
  
  // Filter questions that can be parents (exclude current question if editing)
  const availableParents = questions.filter(q => 
    q.id !== formData.id && 
    ["select", "multiple_select"].includes(q.type)
  );
  
  return (
    <>
      <Field label="Question Text" required>
        <textarea
          dir="ltr"
          value={formData.question}
          onChange={e => onFieldChange("question", e.target.value)}
          className={`${inputCls} resize-none`}
          placeholder="e.g. What noodle brands do you stock?"
          rows={3}
          autoFocus
        />
      </Field>

      <Field label="Question Type" required>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {QUESTION_TYPES.map(type => (
            <button
              key={type.value}
              type="button"
              onClick={() => onTypeChange(type.value)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                formData.type === type.value
                  ? "bg-blue-50 border-blue-200 text-blue-700"
                  : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
              }`}
            >
              <Icon d={type.icon} className="w-4 h-4" />
              {type.label}
            </button>
          ))}
        </div>
      </Field>

      {!isSubQuestion && (
        <Field label="Parent Question (Optional)">
          <select
            value={formData.parent_id || ""}
            onChange={e => onFieldChange("parent_id", e.target.value || null)}
            className={inputCls}
          >
            <option value="">None (Main Question)</option>
            {availableParents.map(parent => (
              <option key={parent.id} value={parent.id}>
                {parent.question?.substring(0, 50)}{parent.question?.length > 50 ? "..." : ""}
              </option>
            ))}
          </select>
          <p className="text-xs text-slate-400 mt-1">
            Make this a follow-up question that appears based on parent question answers
          </p>
        </Field>
      )}

      {isChoiceType && (
        <Field label="Answer Options" required>
          <div className="space-y-2">
            {formData.options.map((opt, i) => (
              <div key={i} className="flex gap-2">
                <input
                  dir="ltr"
                  type="text"
                  value={opt}
                  onChange={e => onUpdateOption(i, e.target.value)}
                  className={inputCls}
                  placeholder={`Option ${i + 1}`}
                />
                <button type="button" onClick={() => onRemoveOption(i)}
                  className="px-3 py-2.5 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors shrink-0">
                  <Icon d={ICONS.x} className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button type="button" onClick={onAddOption}
              className="w-full px-3 py-2.5 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 transition-colors text-sm font-medium flex items-center justify-center gap-1.5">
              <Icon d={ICONS.plus} className="w-4 h-4" />
              Add Option
            </button>
          </div>
        </Field>
      )}
    </>
  );
};

// ─── Buttons — outside for stable identity ────────────────────────────────────
const SubmitBtn = ({ label, loadingLabel, onClick, submitting }) => (
  <button onClick={onClick} disabled={submitting}
    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors">
    {submitting
      ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
      : <Icon d={ICONS.check} className="w-4 h-4" />}
    {submitting ? loadingLabel : label}
  </button>
);

const CancelBtn = ({ onClick }) => (
  <button onClick={onClick}
    className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-colors">
    Cancel
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────
export default function SurveyQuestions() {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions]   = useState([]);
  const [survey, setSurvey]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch]         = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit]     = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showCreateSub, setShowCreateSub] = useState(false);
  const [showCreateOptionSub, setShowCreateOptionSub] = useState(false);
  const [parentForSub, setParentForSub] = useState(null);
  const [optionForSub, setOptionForSub] = useState(null);
  const [toEdit, setToEdit]         = useState(null);
  const [toDelete, setToDelete]     = useState(null);
  const [formData, setFormData]     = useState(EMPTY_FORM);

  const fetchSurveyData = useCallback(async () => {
    try {
      const r = await apiRequest(API_URLS.admin.getSurvey(surveyId));
      if (r.data) { setQuestions(r.data.questions || []); setSurvey(r.data); }
      else setError("Survey not found");
    } catch (err) { setError(err.message || "Failed to fetch survey data"); }
    finally { setLoading(false); }
  }, [surveyId]);

  useEffect(() => { fetchSurveyData(); }, [surveyId, fetchSurveyData]);

  // ── stable form handlers (passed as props, not closures) ──────────────────
  const handleFieldChange = (key, value) => setFormData(f => ({ ...f, [key]: value }));
  const handleTypeChange  = (type) => setFormData(f => {
    const CHOICE_TYPES = ["select", "multiple_select"];
    // Keep existing options when switching between the two choice variants.
    // Only clear them when switching to a non-choice type (text, textarea, number).
    const keepOptions = CHOICE_TYPES.includes(type) && CHOICE_TYPES.includes(f.type);
    return { ...f, type, options: keepOptions ? f.options : [] };
  });
  const handleAddOption    = () => setFormData(f => ({ ...f, options: [...f.options, ""] }));
  const handleUpdateOption = (i, v) => setFormData(f => {
    const opts = [...f.options]; opts[i] = v; return { ...f, options: opts };
  });
  const handleRemoveOption = (i) => setFormData(f => ({
    ...f, options: f.options.filter((_, idx) => idx !== i)
  }));

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const validate = () => {
    if (!formData.question.trim()) { setError("Question text is required"); return false; }
    if (["select","multiple_select"].includes(formData.type) && formData.options.length === 0) {
      setError("Please add at least one option for multiple choice questions"); return false;
    }
    return true;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        question: formData.question,
        type: formData.type,
        options: formData.options,
        parent_id: formData.parent_id,
        option_text: formData.option_text
      };
      await apiRequest(API_URLS.admin.addQuestion(surveyId), "POST", payload);
      setFormData(EMPTY_FORM); setShowCreate(false); fetchSurveyData();
    } catch (err) { setError(err.message || "Failed to add question"); }
    finally { setSubmitting(false); }
  };

  const handleCreateSub = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        question: formData.question,
        type: formData.type,
        options: formData.options,
        parent_id: formData.parent_id,
        option_text: formData.option_text
      };
      await apiRequest(API_URLS.admin.addQuestion(surveyId), "POST", payload);
      setFormData(EMPTY_FORM); setShowCreateSub(false); setParentForSub(null); fetchSurveyData();
    } catch (err) { setError(err.message || "Failed to add follow-up question"); }
    finally { setSubmitting(false); }
  };

  const handleCreateOptionSub = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        question: formData.question,
        type: formData.type,
        options: formData.options,
        parent_id: formData.parent_id,
        option_text: formData.option_text
      };
      await apiRequest(API_URLS.admin.addQuestion(surveyId), "POST", payload);
      setFormData(EMPTY_FORM); setShowCreateOptionSub(false); setParentForSub(null); setOptionForSub(null); fetchSurveyData();
    } catch (err) { setError(err.message || "Failed to add option-specific question"); }
    finally { setSubmitting(false); }
  };

  const handleUpdate = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        question: formData.question,
        type: formData.type,
        options: formData.options,
        parent_id: formData.parent_id,
        option_text: formData.option_text
      };
      await apiRequest(API_URLS.admin.editQuestion(toEdit.id), "PUT", payload);
      setFormData(EMPTY_FORM); setShowEdit(false); setToEdit(null); fetchSurveyData();
    } catch (err) { setError(err.message || "Failed to update question"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await apiRequest(API_URLS.admin.deleteQuestion(toDelete), "DELETE");
      setShowDelete(false); setToDelete(null); fetchSurveyData();
    } catch (err) { setError(err.message || "Failed to delete question"); }
  };

  const openEdit = (q) => {
    setToEdit(q);
    setFormData({ 
      question: q.question, 
      type: q.type, 
      options: q.options || [], 
      parent_id: q.parent_id || null,
      option_text: q.option_text || null,
      id: q.id 
    });
    setShowEdit(true);
  };

  const openCreateSub = (parentQuestion) => {
    setParentForSub(parentQuestion);
    setFormData({ 
      ...EMPTY_FORM, 
      parent_id: parentQuestion.id
    });
    setShowCreateSub(true);
  };

  const openCreateOptionSub = (parentQuestion, optionText) => {
    setParentForSub(parentQuestion);
    setOptionForSub(optionText);
    setFormData({ 
      ...EMPTY_FORM, 
      parent_id: parentQuestion.id,
      option_text: optionText
    });
    setShowCreateOptionSub(true);
  };

  // ── derived ───────────────────────────────────────────────────────────────
  const filtered = questions.filter(q => 
    !q.option_text && // Filter out option-specific questions from main list
    (!search || q.question?.toLowerCase().includes(search.toLowerCase()))
  );
  const toDeleteText = questions.find(q => q.id === toDelete)?.question;

  // shared props bundle for the form
  const formProps = {
    formData,
    onFieldChange:  handleFieldChange,
    onTypeChange:   handleTypeChange,
    onAddOption:    handleAddOption,
    onUpdateOption: handleUpdateOption,
    onRemoveOption: handleRemoveOption,
    questions: questions,
  };

  // ─────────────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex flex-col items-center justify-center h-72 gap-4">
      <div className="relative w-12 h-12">
        <div className="absolute inset-0 rounded-full border-4 border-blue-100" />
        <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
      </div>
      <p className="text-sm text-slate-400 font-medium">Loading questions…</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">

      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 px-6 py-5 sticky top-0 z-10 shadow-sm">
        <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/surveys")}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
              <Icon d={ICONS.arrowLeft} className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">{survey?.title || "Survey Questions"}</h1>
              <p className="text-xs text-slate-400 mt-0.5">{survey?.description || "Manage survey questions"}</p>
            </div>
          </div>
          <button onClick={() => { setFormData(EMPTY_FORM); setShowCreate(true); }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors">
            <Icon d={ICONS.plus} className="w-4 h-4" />
            Add Question
          </button>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {error && <Alert type="error" message={error} onClose={() => setError("")} />}

        {/* ── Summary strip ────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total Questions", value: questions.length,                                                          color: "bg-blue-600",    icon: ICONS.questions },
            { label: "Short Text",      value: questions.filter(q => q.type === "text").length,                           color: "bg-emerald-600",  icon: ICONS.text      },
            { label: "Multiple Choice", value: questions.filter(q => ["select","multiple_select"].includes(q.type)).length,color: "bg-violet-600",   icon: ICONS.select    },
            { label: "Showing",         value: filtered.length,                                                            color: "bg-slate-400",    icon: ICONS.hash      },
          ].map((c, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
              <div className={`w-9 h-9 ${c.color} rounded-lg flex items-center justify-center shrink-0`}>
                <Icon d={c.icon} className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-900 leading-none">{c.value}</p>
                <p className="text-xs text-slate-500 mt-1 font-medium">{c.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Questions list ─────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Toolbar */}
          <div className="px-5 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-slate-800">All Questions</h2>
              <p className="text-xs text-slate-400 mt-0.5">{filtered.length} of {questions.length} questions</p>
            </div>
            <div className="relative max-w-xs w-full sm:w-auto">
              <Icon d={ICONS.search} className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input dir="ltr" type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search questions…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition" />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <Icon d={ICONS.x} className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Body */}
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-400">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                <Icon d={ICONS.questions} className="w-6 h-6 text-slate-300" />
              </div>
              <p className="text-sm font-medium">{search ? `No questions match "${search}"` : "No questions yet"}</p>
              {!search && (
                <button onClick={() => { setFormData(EMPTY_FORM); setShowCreate(true); }}
                  className="text-xs text-blue-600 hover:text-blue-800 font-semibold">
                  Add your first question →
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {[...filtered].map((question, i) => {
                const typeInfo = QUESTION_TYPES.find(t => t.value === question.type);
                const isSubQuestion = question.parent_id !== null;
                const isOptionSubQuestion = question.option_text !== null;
                
                return (
                  <div key={question.id} className={`${isSubQuestion ? 'ml-8 border-l-2 border-blue-200' : ''}`}>
                    <div className={`flex items-start gap-4 px-5 py-4 hover:bg-slate-50/60 transition-colors ${isSubQuestion ? 'pl-8' : ''}`}>
                      <div className={`w-8 h-8 ${PALETTE[i % PALETTE.length]} rounded-lg flex items-center justify-center text-white text-sm font-bold shrink-0 mt-0.5 ${isSubQuestion ? (isOptionSubQuestion ? 'bg-green-400' : 'bg-blue-400') : ''}`}>
                        {isSubQuestion ? (isOptionSubQuestion ? '→' : '↓') : (i + 1)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          {isSubQuestion && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${isOptionSubQuestion ? 'bg-green-50 text-green-700 border-green-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                              {isOptionSubQuestion ? 'Option Follow-up' : 'Follow-up'}
                            </span>
                          )}
                          {isOptionSubQuestion && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-200">
                              For: "{question.option_text}"
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-blue-50 text-blue-700 border-blue-200">
                            <Icon d={typeInfo?.icon || ICONS.hash} className="w-3 h-3" />
                            {typeInfo?.label || question.type}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-slate-800 mb-1.5">{question.question}</p>
                        {["select","multiple_select"].includes(question.type) && question.options?.length > 0 && (
                          <div className="space-y-1">
                            {question.options.map((opt, optIdx) => {
                              const optionSubQuestions = questions.filter(q => 
                                q.parent_id === question.id && q.option_text === opt
                              );
                              
                              return (
                                <div key={optIdx} className="space-y-1">
                                  <div className="flex items-center gap-2 text-xs text-slate-500">
                                    <span className="w-1.5 h-1.5 bg-slate-300 rounded-full shrink-0" />
                                    {opt}
                                    {!isSubQuestion && ["select","multiple_select"].includes(question.type) && (
                                      <button
                                        onClick={() => openCreateOptionSub(question, opt)}
                                        className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-green-50 text-green-700 border border-green-100 hover:bg-green-100 transition-colors"
                                      >
                                        <Icon d={ICONS.plus} className="w-3 h-3" />
                                        Add Question
                                      </button>
                                    )}
                                  </div>
                                  
                                  {/* Display sub-questions for this option */}
                                  {optionSubQuestions.map((subQ, subIdx) => {
                                    const subTypeInfo = QUESTION_TYPES.find(t => t.value === subQ.type);
                                    const isSubChoiceType = ["select", "multiple_select"].includes(subQ.type);
                                    
                                    return (
                                      <div key={subQ.id} className="ml-6 pl-4 border-l-2 border-green-200">
                                        <div className="flex items-start gap-3 py-2">
                                          <div className="w-6 h-6 bg-green-400 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0">
                                            →
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border-green-200">
                                                Option Follow-up
                                              </span>
                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border-blue-200">
                                                <Icon d={subTypeInfo?.icon || ICONS.hash} className="w-3 h-3" />
                                                {subTypeInfo?.label || subQ.type}
                                              </span>
                                            </div>
                                            <p className="text-sm font-semibold text-slate-800 mb-1">{subQ.question}</p>
                                            
                                            {/* Display options for sub-questions if they have them */}
                                            {isSubChoiceType && subQ.options?.length > 0 && (
                                              <div className="space-y-1 mb-2">
                                                {subQ.options.map((subOpt, subOptIdx) => (
                                                  <div key={subOptIdx} className="flex items-center gap-2 text-xs text-slate-500">
                                                    <span className="w-1 h-1 bg-slate-300 rounded-full shrink-0" />
                                                    {subOpt}
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                            
                                            <div className="flex items-center gap-2">
                                              <button onClick={() => openEdit(subQ)}
                                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100 transition-colors">
                                                <Icon d={ICONS.edit} className="w-3 h-3" />Edit
                                              </button>
                                              <button onClick={() => { setToDelete(subQ.id); setShowDelete(true); }}
                                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-red-50 text-red-600 border border-red-100 hover:bg-red-100 transition-colors">
                                                <Icon d={ICONS.trash} className="w-3 h-3" />Delete
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0 pt-0.5">
                        {!isSubQuestion && ["select","multiple_select"].includes(question.type) && (
                          <button onClick={() => openCreateSub(question)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-50 text-green-700 border border-green-100 hover:bg-green-100 transition-colors">
                            <Icon d={ICONS.plus} className="w-3.5 h-3.5" />Add Follow-up
                          </button>
                        )}
                        <button onClick={() => openEdit(question)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100 transition-colors">
                          <Icon d={ICONS.edit} className="w-3.5 h-3.5" />Edit
                        </button>
                        <button onClick={() => { setToDelete(question.id); setShowDelete(true); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-600 border border-red-100 hover:bg-red-100 transition-colors">
                          <Icon d={ICONS.trash} className="w-3.5 h-3.5" />Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Create Modal ─────────────────────────────────────────────────── */}
      {showCreate && (
        <Modal icon={ICONS.plus} iconBg="bg-blue-600"
          title="Add Question" subtitle="Create a new question for this survey"
          onClose={() => { setShowCreate(false); setFormData(EMPTY_FORM); }}
          footer={
            <div className="flex gap-3">
              <SubmitBtn label="Add Question" loadingLabel="Adding…" onClick={handleCreate} submitting={submitting} />
              <CancelBtn onClick={() => { setShowCreate(false); setFormData(EMPTY_FORM); }} />
            </div>
          }>
          <QuestionFormBody {...formProps} />
        </Modal>
      )}

      {/* ── Create Sub-Question Modal ─────────────────────────────────────────── */}
      {showCreateSub && (
        <Modal icon={ICONS.plus} iconBg="bg-green-600"
          title="Add Follow-up Question" subtitle={`Adding follow-up to: ${parentForSub?.question?.substring(0, 50)}${parentForSub?.question?.length > 50 ? "..." : ""}`}
          onClose={() => { setShowCreateSub(false); setParentForSub(null); setFormData(EMPTY_FORM); }}
          footer={
            <div className="flex gap-3">
              <SubmitBtn label="Add Follow-up" loadingLabel="Adding…" onClick={handleCreateSub} submitting={submitting} />
              <CancelBtn onClick={() => { setShowCreateSub(false); setParentForSub(null); setFormData(EMPTY_FORM); }} />
            </div>
          }>
          <QuestionFormBody {...formProps} isSubQuestion={true} />
        </Modal>
      )}

      {/* ── Create Option Sub-Question Modal ─────────────────────────────────── */}
      {showCreateOptionSub && (
        <Modal icon={ICONS.plus} iconBg="bg-green-600"
          title="Add Option-Specific Question" subtitle={`For option: "${optionForSub}" in ${parentForSub?.question?.substring(0, 30)}${parentForSub?.question?.length > 30 ? "..." : ""}`}
          onClose={() => { setShowCreateOptionSub(false); setParentForSub(null); setOptionForSub(null); setFormData(EMPTY_FORM); }}
          footer={
            <div className="flex gap-3">
              <SubmitBtn label="Add Question" loadingLabel="Adding…" onClick={handleCreateOptionSub} submitting={submitting} />
              <CancelBtn onClick={() => { setShowCreateOptionSub(false); setParentForSub(null); setOptionForSub(null); setFormData(EMPTY_FORM); }} />
            </div>
          }>
          <QuestionFormBody {...formProps} isSubQuestion={true} />
        </Modal>
      )}

      {/* ── Edit Modal ───────────────────────────────────────────────────── */}
      {showEdit && (
        <Modal icon={ICONS.edit} iconBg="bg-indigo-600"
          title="Edit Question" subtitle={`Updating: Q${questions.findIndex(q => q.id === toEdit?.id) + 1}`}
          onClose={() => { setShowEdit(false); setToEdit(null); setFormData(EMPTY_FORM); }}
          footer={
            <div className="flex gap-3">
              <SubmitBtn label="Save Changes" loadingLabel="Saving…" onClick={handleUpdate} submitting={submitting} />
              <CancelBtn onClick={() => { setShowEdit(false); setToEdit(null); setFormData(EMPTY_FORM); }} />
            </div>
          }>
          <QuestionFormBody {...formProps} />
        </Modal>
      )}

      {/* ── Delete Modal ─────────────────────────────────────────────────── */}
      {showDelete && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="flex flex-col items-center text-center px-6 pt-8 pb-5">
              <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mb-4 ring-8 ring-red-50">
                <Icon d={ICONS.warning} className="w-7 h-7 text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-slate-900">Delete Question</h2>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                Are you sure you want to delete this question?
                <span className="block font-semibold text-slate-800 mt-1 truncate max-w-xs">"{toDeleteText}"</span>
                This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button onClick={handleDelete}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition-colors">
                <Icon d={ICONS.trash} className="w-4 h-4" />Delete
              </button>
              <button onClick={() => { setShowDelete(false); setToDelete(null); }}
                className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}